import type {
  CmmdAnchorKind,
  CmmdRegistration,
  EnvironmentId,
  ModelSelection,
  OrchestrationLatestTurn,
  OrchestrationProposedPlanId,
  ProjectAvailability,
  RepositoryIdentity,
  OrchestrationSessionStatus,
  OrchestrationThreadActivity,
  ProjectScript as ContractProjectScript,
  ThreadEnvMode,
  ThreadId,
  ProjectId,
  ThreadKind,
  ThreadStatus,
  ThreadTitleRegeneration,
  TurnId,
  MessageId,
  ProviderKind,
  CheckpointRef,
  ProviderInteractionMode,
  RuntimeMode,
  LinkedPullRequest,
} from "@cmmd-forge/contracts";

export type { CmmdAnchorKind, ThreadKind, ThreadStatus };

export type SessionPhase = "disconnected" | "connecting" | "ready" | "running";
export const DEFAULT_RUNTIME_MODE: RuntimeMode = "full-access";

export const DEFAULT_INTERACTION_MODE: ProviderInteractionMode = "default";
export const DEFAULT_THREAD_TERMINAL_HEIGHT = 280;
export const DEFAULT_THREAD_TERMINAL_ID = "default";
export const MAX_TERMINALS_PER_GROUP = 4;
export type ProjectScript = ContractProjectScript;

export interface ThreadTerminalGroup {
  id: string;
  terminalIds: string[];
}

export interface ChatImageAttachment {
  type: "image";
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  previewUrl?: string;
}

export interface ChatFileAttachment {
  type: "file";
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  downloadUrl: string;
}

export type ChatAttachment = ChatImageAttachment | ChatFileAttachment;

export interface ChatMessage {
  readonly sourceEventId?: string;
  id: MessageId;
  /**
   * "reasoning" is the provider thinking-trace role (ticket 3525, upstream
   * parity). The wire contract does not emit it yet; the timeline renders the
   * role when adapters start sending it.
   */
  role: "user" | "assistant" | "system" | "reasoning";
  text: string;
  attachments?: ChatAttachment[];
  turnId?: TurnId | null;
  createdAt: string;
  completedAt?: string | undefined;
  /** Last provider update time (upstream parity); falls back to completedAt. */
  updatedAt?: string | undefined;
  streaming: boolean;
}

export interface ProposedPlan {
  id: OrchestrationProposedPlanId;
  turnId: TurnId | null;
  planMarkdown: string;
  implementedAt: string | null;
  implementationThreadId: ThreadId | null;
  createdAt: string;
  updatedAt: string;
}

export interface TurnDiffFileChange {
  path: string;
  kind?: string | undefined;
  additions?: number | undefined;
  deletions?: number | undefined;
}

export interface TurnDiffSummary {
  turnId: TurnId;
  completedAt: string;
  status?: string | undefined;
  files: TurnDiffFileChange[];
  checkpointRef?: CheckpointRef | undefined;
  assistantMessageId?: MessageId | undefined;
  checkpointTurnCount?: number | undefined;
}

export interface Project {
  id: ProjectId;
  environmentId: EnvironmentId;
  name: string;
  cwd: string;
  repositoryIdentity?: RepositoryIdentity | null;
  /**
   * Whether the project's `cwd` currently exists on the host filesystem.
   * Server-side probe; `undefined` for legacy snapshots / events that
   * don't carry it. Treat `undefined` and `"unknown"` the same.
   */
  availability?: ProjectAvailability | undefined;
  defaultModelSelection: ModelSelection | null;
  /**
   * The Registration this Checkout was created from, when it was created from
   * one. It is the durable identity of a Checkout on an Environment and the key
   * every cloud lookup uses; absent for a project added by hand and for every
   * project created before the field existed, which the directory rules still
   * match.
   */
  cmmdRegistration?: CmmdRegistration | null;
  /** Where this project's new threads start; null means "use the app setting". */
  defaultThreadEnvMode?: ThreadEnvMode | null;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  scripts: ProjectScript[];
}

export interface Thread {
  id: ThreadId;
  environmentId: EnvironmentId;
  codexThreadId: string | null;
  projectId: ProjectId;
  title: string;
  modelSelection: ModelSelection;
  runtimeMode: RuntimeMode;
  interactionMode: ProviderInteractionMode;
  session: ThreadSession | null;
  messages: ChatMessage[];
  proposedPlans: ProposedPlan[];
  error: string | null;
  createdAt: string;
  archivedAt: string | null;
  /** CMMD-owned resource archive workflow. Null or absent means no archive operation. */
  archive?: import("@cmmd-forge/contracts").CmmdThreadArchiveSummary | null | undefined;
  /** Set by an explicit settle, cleared by archive/unarchive or an explicit unsettle. */
  settledAt: string | null;
  /** A user's durable settle/unsettle choice, overriding the silence-based default. Null means no override. */
  settledOverride: import("@cmmd-forge/contracts").SettledOverride;
  updatedAt?: string | undefined;
  latestTurn: OrchestrationLatestTurn | null;
  pendingSourceProposedPlan?: OrchestrationLatestTurn["sourceProposedPlan"];
  branch: string | null;
  worktreePath: string | null;
  turnDiffSummaries: TurnDiffSummary[];
  activities: OrchestrationThreadActivity[];
  /** CMMD anchor kind. 'scratch' = local-only, no CMMD link. */
  anchorKind: CmmdAnchorKind;
  /** CMMD entity ID. Null for scratch threads. */
  anchorCmmdId: string | null;
  /** Durable identity only; Command remains authoritative for live PR state. */
  linkedPullRequest?: LinkedPullRequest | null | undefined;
  /** Thread kind (M3). 'master' for top-level, 'worker' for spawned sub-threads. */
  kind: ThreadKind;
  /** For worker threads, the master that spawned them. Null for masters. */
  parentThreadId: ThreadId | null;
  /** Orchestration lifecycle status (M3). */
  status: ThreadStatus;
  /**
   * Worker specialty (WorkBar). Null for masters; null for workers not yet tagged
   * with a role (legacy rows decode as null via contracts `withDecodingDefault`).
   */
  workerRole: import("@cmmd-forge/contracts").WorkerRole | null;
  /**
   * Free-text session goal/intent. Null when unset.
   */
  goal: string | null;
  /**
   * In-flight title regeneration. Absent and null mean the same thing here —
   * no regeneration running — so view-model fixtures need not carry it. The
   * required-with-default form lives on the contract, where the distinction
   * between "missing column" and "none" actually has to be resolved.
   */
  titleRegeneration?: ThreadTitleRegeneration | null | undefined;
  /**
   * Pending auto-create task prompt (M4.5). Set when the master proposes a
   * plan and autoCreateTasks==='ask'. Null/absent once approved/discarded.
   */
  pendingAutoCreatePrompt?: import("@cmmd-forge/contracts").AutoCreateTaskPrompt | null | undefined;
  /**
   * Last-known 5h/7d rate-limit telemetry per provider this thread has used,
   * from real turns only (never a manufactured check). Absent/[] means no
   * signal has arrived yet, not "not rate limited".
   */
  rateLimitStatus?:
    | ReadonlyArray<import("@cmmd-forge/contracts").ThreadProviderRateLimitStatus>
    | undefined;
}

export interface ThreadShell {
  id: ThreadId;
  environmentId: EnvironmentId;
  codexThreadId: string | null;
  projectId: ProjectId;
  title: string;
  modelSelection: ModelSelection;
  runtimeMode: RuntimeMode;
  interactionMode: ProviderInteractionMode;
  error: string | null;
  createdAt: string;
  archivedAt: string | null;
  /** CMMD-owned resource archive workflow. Null or absent means no archive operation. */
  archive?: import("@cmmd-forge/contracts").CmmdThreadArchiveSummary | null | undefined;
  /** Set by an explicit settle, cleared by archive/unarchive or an explicit unsettle. */
  settledAt: string | null;
  /** A user's durable settle/unsettle choice, overriding the silence-based default. Null means no override. */
  settledOverride: import("@cmmd-forge/contracts").SettledOverride;
  updatedAt?: string | undefined;
  branch: string | null;
  worktreePath: string | null;
  anchorKind: CmmdAnchorKind;
  anchorCmmdId: string | null;
  /** Durable identity only; Command remains authoritative for live PR state. */
  linkedPullRequest?: LinkedPullRequest | null | undefined;
  /** Thread kind (M3). 'master' for top-level, 'worker' for spawned sub-threads. */
  kind: ThreadKind;
  /** For worker threads, the master that spawned them. Null for masters. */
  parentThreadId: ThreadId | null;
  /** Orchestration lifecycle status (M3). */
  status: ThreadStatus;
  /** Worker specialty (WorkBar). Null for masters or untagged workers. */
  workerRole: import("@cmmd-forge/contracts").WorkerRole | null;
  /** Free-text session goal. Null when unset. */
  goal: string | null;
  /**
   * In-flight title regeneration. Absent and null mean the same thing here —
   * no regeneration running — so view-model fixtures need not carry it. The
   * required-with-default form lives on the contract, where the distinction
   * between "missing column" and "none" actually has to be resolved.
   */
  titleRegeneration?: ThreadTitleRegeneration | null | undefined;
}

export interface ThreadTurnState {
  latestTurn: OrchestrationLatestTurn | null;
  pendingSourceProposedPlan?: OrchestrationLatestTurn["sourceProposedPlan"];
}

export interface SidebarThreadSummary {
  id: ThreadId;
  environmentId: EnvironmentId;
  projectId: ProjectId;
  title: string;
  /**
   * The thread's own durable model selection, when we have one. Carried
   * because `session` is the only other place a row can learn its agent, and a
   * session exists only while something is running — so an idle thread named
   * no agent at all. Upstream's summary IS the thread shell
   * (`SidebarThreadSummary = EnvironmentThreadShell`), so it never lost this.
   *
   * Optional, and deliberately so: threads projected from the CMMD runtime
   * inventory genuinely have no provider to report. That payload carries a
   * `model` slug but no provider kind, and the glyph map is keyed by provider,
   * so a cloud row omits this rather than inventing one from the slug.
   */
  modelSelection?: ModelSelection | undefined;
  interactionMode: ProviderInteractionMode;
  session: ThreadSession | null;
  createdAt: string;
  archivedAt: string | null;
  /** CMMD-owned resource archive workflow. Null or absent means no archive operation. */
  archive?: import("@cmmd-forge/contracts").CmmdThreadArchiveSummary | null | undefined;
  /** Set by an explicit settle, cleared by archive/unarchive or an explicit unsettle. */
  settledAt: string | null;
  /** A user's durable settle/unsettle choice, overriding the silence-based default. Null means no override. */
  settledOverride: import("@cmmd-forge/contracts").SettledOverride;
  updatedAt?: string | undefined;
  latestTurn: OrchestrationLatestTurn | null;
  branch: string | null;
  worktreePath: string | null;
  latestUserMessageAt: string | null;
  hasPendingApprovals: boolean;
  hasPendingUserInput: boolean;
  hasActionableProposedPlan: boolean;
  /** CMMD anchor kind. 'scratch' = local-only thread. Defaults to 'scratch'. */
  anchorKind: CmmdAnchorKind;
  /** CMMD entity ID. Null for scratch threads. */
  anchorCmmdId: string | null;
  /** Durable identity only; Command remains authoritative for live PR state. */
  linkedPullRequest?: LinkedPullRequest | null | undefined;
  /** Stable free-text goal used to identify role-specific conversations independently of title. */
  goal?: string | null;
  /** In-flight title regeneration; the row renders as busy while set. */
  titleRegeneration?: ThreadTitleRegeneration | null | undefined;
  /** Thread kind (M3). 'master' for top-level, 'worker' for spawned sub-threads. */
  kind: ThreadKind;
  /** For worker threads, the master that spawned them. Null for masters. */
  parentThreadId: ThreadId | null;
  /** Orchestration lifecycle status (M3). */
  status: ThreadStatus;
  /** Worker specialty (WorkBar). Null for masters or untagged workers. */
  workerRole: import("@cmmd-forge/contracts").WorkerRole | null;
}

export interface ThreadSession {
  provider: ProviderKind;
  status: SessionPhase | "error" | "closed";
  activeTurnId?: TurnId | undefined;
  createdAt: string;
  updatedAt: string;
  lastError?: string;
  orchestrationStatus: OrchestrationSessionStatus;
}
